import { Injectable, Injector } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import {
  BehaviorSubject,
  concatMap,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { NotificationComponent } from '../../content/common/notification/notification.component';
import { SnackbarMessageType } from '../enums';
import { SnackbarData } from '../models/snackbar-data';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class SnackBarService {
  messageCallsQueueLength$ = new BehaviorSubject<number>(0);
  currSnackbar$ =
    new BehaviorSubject<MatSnackBarRef<NotificationComponent> | null>(null);
  messageCallsQueue = new Subject<SnackbarData>();

  defaultSnackbarLifeMS = 4000;
  lastMessage?: string;
  queueSubscription?: Subscription;
  skipCurrent = new Subject<void>();

  constructor(private snackBar: MatSnackBar, private injector: Injector) {
    this.initQueueListener();
  }

  initQueueListener(): void {
    this.queueSubscription = this.messageCallsQueue
      .pipe(
        concatMap((data) => {
          const newSnackBar = this._open(data);
          this.currSnackbar$.next(newSnackBar);
          return newSnackBar.afterDismissed().pipe(takeUntil(this.skipCurrent));
        })
      )
      .subscribe();
  }

  resetQueue(): void {
    this.queueSubscription?.unsubscribe();
    this.messageCallsQueueLength$.next(0);
    this.currSnackbar$.value?.dismiss();
    this.currSnackbar$.next(null);
    this.initQueueListener();
  }

  private _open(data: SnackbarData) {
    return this.snackBar.openFromComponent<NotificationComponent, SnackbarData>(
      NotificationComponent,
      {
        data,
        panelClass: ['ling-mat-snack-bar', `ling-mat-snack-bar-${data.type}`],
        verticalPosition: 'top',
        horizontalPosition: 'center',
        duration: data.duration,
      }
    );
  }

  open(
    message: string,
    type: SnackbarMessageType = SnackbarMessageType.Success,
    duration = this.defaultSnackbarLifeMS
  ) {
    if (this.lastMessage === message && this.currSnackbar$.value) {
      return;
    }
    this.lastMessage = message;
    this.messageCallsQueueLength$.next(this.messageCallsQueueLength$.value + 1);
    this.messageCallsQueue.next({
      message,
      type,
      queuePosition: this.messageCallsQueueLength$.value,
      duration,
    });
  }

  success(message: string, duration = this.defaultSnackbarLifeMS) {
    this.open(message, SnackbarMessageType.Success, duration);
  }

  warn(message: string, duration = this.defaultSnackbarLifeMS) {
    this.open(message, SnackbarMessageType.Warn, duration);
  }

  error(message: string, duration = this.defaultSnackbarLifeMS) {
    this.open(message, SnackbarMessageType.Error, duration);
  }

  required() {
    // Needed to be injected this way to avoid DI order collision for this service
    const translateService = this.injector.get(TranslateService);
    this.warn(translateService.instant('snackbar.warn.formInvalid'), 2000);
    const errorInput = document.querySelector('.error-input');
    errorInput?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }
}
